import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingDown, FolderTree, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFinancialWidgets } from '@/hooks/use-financial-widgets';
import { Skeleton } from '@/components/ui/skeleton';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

interface DepreciationByCategoryCardProps {
  readonly className?: string;
}

interface CustomTooltipProps {
  readonly active?: boolean;
  readonly payload?: Array<{ name: string; value: number; payload: { percentage: string } }>;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (active && payload?.length) {
    const data = payload[0];
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border">
        <p className="font-semibold mb-1">{data.name}</p>
        <p className="text-sm">{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(data.value)}</p>
        <p className="text-xs text-muted-foreground">{data.payload.percentage}%</p>
      </div>
    );
  }
  return null;
};

interface RenderLabelEntry {
  readonly value: number;
}

const renderCustomLabel = (entry: RenderLabelEntry) => {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(entry.value);
};

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#f97316', '#6366f1'];

export function DepreciationByCategoryCard({ className }: DepreciationByCategoryCardProps): React.ReactElement {
  const { useDepreciationByCategory } = useFinancialWidgets();
  const { data, isLoading, error } = useDepreciationByCategory();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (isLoading) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm bg-white dark:bg-black', className)}>
        <CardHeader>
          <CardTitle className="text-base font-semibold tracking-tight">Depreciation by Category</CardTitle>
          <CardDescription>Total depreciation breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data || data.categories.length === 0) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm bg-white dark:bg-black', className)}>
        <CardHeader>
          <CardTitle className="text-base font-semibold tracking-tight">Depreciation by Category</CardTitle>
          <CardDescription>Total depreciation breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">No depreciation data available.</div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.categories.map(category => ({
    name: category.category,
    value: category.totalDepreciation,
    percentage: ((category.totalDepreciation / data.totalDepreciation) * 100).toFixed(1),
  }));

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col', className)}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Depreciation by Category</CardTitle>
            <CardDescription>Total depreciation breakdown</CardDescription>
          </div>
          <div className="p-2 bg-red-50 dark:bg-red-950 rounded-lg">
            <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {/* Two Column Layout */}
        <div className="grid grid-cols-2 gap-6">
          {/* Left Column: KPIs */}
          <div className="space-y-4">
            {/* Total Depreciation */}
            <div className="p-4 rounded-lg border bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950 dark:to-orange-950">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-muted-foreground">Total Depreciation</div>
                <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="text-3xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(data.totalDepreciation)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                All time depreciation
              </div>
            </div>

            {/* Average Depreciation Rate */}
            <div className="p-4 rounded-lg border bg-card">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                <div className="text-sm text-muted-foreground">Avg Depreciation Rate</div>
              </div>
              <div className="text-2xl font-bold">
                {data.categories.length > 0 
                  ? (data.categories.reduce((sum, c) => sum + c.depreciationRate, 0) / data.categories.length).toFixed(1)
                  : 0}%
              </div>
            </div>

            {/* Categories Count */}
            <div className="p-4 rounded-lg border bg-card">
              <div className="flex items-center gap-2 mb-2">
                <FolderTree className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <div className="text-sm text-muted-foreground">Categories</div>
              </div>
              <div className="text-2xl font-bold">{data.categories.length}</div>
              <div className="text-xs text-muted-foreground mt-1">
                Asset categories tracked
              </div>
            </div>
          </div>

          {/* Right Column: Chart */}
          <div className="space-y-3">
            <div className="text-sm font-semibold">Breakdown by Category</div>
            <ResponsiveContainer width="100%" height={340}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="45%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                >
                    {chartData.map((entry) => (
                      <Cell key={entry.name} fill={COLORS[chartData.indexOf(entry) % COLORS.length]} />
                    ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ fontSize: '11px' }}
                  iconType="circle"
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
